import { ProjectJSON, ProjectStatus } from '@projects/models/project';
import { ProjectMemberRoleType } from '@projectMembers/models/projectRole';
import ReduxTanStackSync from 'src/new_arch/services/ReduxTanStackSync';
import Project from 'src/entities/project';
import { FOLDERS_URL, PROJECTS_URL, REQUEST_SEATS_FOR_URL } from '../../../../config/urls';

import Api from '../../../../services/Api';

import AssetFilters from '../../projectView/models/filtersAndSorts/assetFilters';
import ItemSorting from '../../projectView/models/filtersAndSorts/itemSorting';
import FolderTreeJson from '../../projectView/models/folderTreeJson';

export type ProjectPermissions = {
	downloads_enabled_for_reviewers?: boolean,
	see_past_comments_enabled_for_reviewers?: boolean
}

type ItemsCountFetchParams = {
	projectID: number,
	folderID: number | null,
	filters?: AssetFilters,
	sorting?: ItemSorting,
	deep?: boolean
};

type ItemsCountFetchResult = {
	count: number
}

type ProjectCountsJSON = {
	active: number,
	archived: number
}

type RequestSeatsForSuccess = {
	status: 'successful';
};

type RequestSeatsForError = {
	error: string;
	error_message: string;
};

type RequestSeatsForResponse = RequestSeatsForSuccess | RequestSeatsForError;

type RequestUpgradeRoleForSuccess = {
	status: 'successful';
};

type RequestUpgradeRoleForError = {
	error: string;
	error_message: string;
};

type RequestUpgradeRoleForResponse = RequestUpgradeRoleForSuccess | RequestUpgradeRoleForError;

type RequestProjectAccessSuccess = {
	status: 'successful';
};

type RequestProjectAccessError = {
	error: string;
	error_message: string;
};

type RequestProjectAccessResponse = RequestProjectAccessSuccess | RequestProjectAccessError;

export default class ProjectSystem {
	private api: Api;
	private sync: ReduxTanStackSync;

	constructor( { api, sync }: { api: Api, sync: ReduxTanStackSync } ) {
		this.api = api;
		this.sync = sync;
	}

	async fetchProjectCounts(): Promise<ProjectCountsJSON> {
		const projectCountsUrl = `${PROJECTS_URL}/counts`;
		const { response } = await ( this.api.get( projectCountsUrl ) as Promise<{ response: ProjectCountsJSON}> );
		return response;
	}

	async fetchProjects( { status }: { status: ProjectStatus } ): Promise<ProjectJSON[]> {
		const projectsUrl = `${PROJECTS_URL}/${status === 'hidden' ? 'archived' : 'active'}`;
		const { response } = await ( this.api.get( projectsUrl ) as Promise<{ response: ProjectJSON[] }> );
		return response;
	}

	async fetchItemsCount( {
		projectID,
		folderID,
		filters,
		deep = false
	}: ItemsCountFetchParams ): Promise<ItemsCountFetchResult> {
		const baseUrl = this.baseUrlFor( { projectID, folderID } );
		const queryParams = this.itemsCountQueryParamsFor( { forFolders: !!folderID, filters, deep } );
		const itemsCountURL = `${baseUrl}/items_count?${queryParams}`;

		const { response } = await (
			this.api.get( itemsCountURL ) as Promise<{ response: ItemsCountFetchResult }>
		);

		return response;
	}

	async fetchFolderTree( projectID: number ): Promise<FolderTreeJson> {
		const baseUrl = this.baseUrlFor( { projectID, folderID: null } );
		const folderTreeUrl = `${baseUrl}/folders_tree`;

		const { response } = await (
			this.api.get( folderTreeUrl ) as Promise<{ response: FolderTreeJson }>
		);

		return response;
	}

	async fetchIncludedFeatures( projectID: number ) {
		const baseUrl = this.baseUrlFor( { projectID, folderID: null } );
		const includedFeaturesUrl = `${baseUrl}/included_features`;

		const { response } = await (
			this.api.get( includedFeaturesUrl ) as Promise<{ response: string[] }>
		);

		return response;
	}

	pinProject( projectID: number ) {
		return this.pinOrUnpinProject( projectID, 'pin' );
	}

	unpinProject( projectID: number ) {
		return this.pinOrUnpinProject( projectID, 'unpin' );
	}

	async requestSeatsFor(
		{ emails, project }: { emails: string[], project: Project }
	): Promise<RequestSeatsForResponse> {
		const { response } = await (
			this.api.post(
				REQUEST_SEATS_FOR_URL,
				{ emails, project_id: project.id }
			) as Promise<{ response: RequestSeatsForResponse }>
		);
		return response;
	}

	async accessProjectAsManager( { projectID }: { projectID: number } ) {
		const baseUrl = this.baseUrlFor( { projectID, folderID: null } );
		await ( this.api.post( `${baseUrl}/access_as_manager` ) as Promise<unknown> );
		this.sync.updateCurrentUserRoleInProject( projectID, ProjectMemberRoleType.ProjectManager );
	}

	async updateProjectPermissions( {
		projectID, permissions
	}: { projectID: number, permissions: ProjectPermissions } ) {
		const url = `${PROJECTS_URL}/${projectID}/permissions`;
		await ( this.api.put( url, { permissions } ) as Promise<unknown> );

		this.sync.updateProjectProperties( projectID, permissions );
	}

	async requestRoleUpgrade(
		{ projectID, source }: { projectID: number, source: string }
	): Promise< RequestUpgradeRoleForResponse> {
		const baseUrl = this.baseUrlFor( { projectID, folderID: null } );
		const requestUpgradeRoleForUrl = `${baseUrl}/request_to_upgrade_role`;
		const { response } = await (
			this.api.post( requestUpgradeRoleForUrl, { source } ) as
				Promise<{ response: RequestUpgradeRoleForResponse }>
		);
		return response;
	}

	async requestProjectAccess(
		{ projectID }: { projectID: number }
	): Promise< RequestProjectAccessResponse> {
		const baseUrl = this.baseUrlFor( { projectID, folderID: null } );
		const requestAccessUrl = `${baseUrl}/request_access`;
		const { response } = await (
			this.api.post( requestAccessUrl ) as Promise<{ response: RequestProjectAccessResponse }>
		);
		return response;
	}

	private async pinOrUnpinProject( projectID: number, action: 'pin' | 'unpin' ) {
		const baseUrl = this.baseUrlFor( { projectID, folderID: null } );

		return ( this.api.post( `${baseUrl}/${action}` ) as Promise<unknown> );
	}

	private baseUrlFor( { projectID, folderID }: { projectID: number, folderID: number | null } ) {
		return folderID
			? `${FOLDERS_URL}/${folderID}`
			: `${PROJECTS_URL}/${projectID}`;
	}

	private itemsCountQueryParamsFor( {
		forFolders, filters, deep
	}: {
		forFolders: boolean, filters?: AssetFilters, deep?: boolean
	} ) {
		const params = new URLSearchParams( {} );
		const filtersParamName = forFolders ? 'asset_filters' : 'filters';

		if ( filters ) params.append( filtersParamName, filters.toJsonString() );
		if ( deep ) params.append( 'deep', 'true' );

		return params.toString();
	}
}
